import { User } from '../../lib/user'
import { Cell } from '../grid/Cell'
import { getPuzzle } from '../../lib/words'
import { Puzzle } from '../puzzle/Puzzle'
import { CogIcon } from '@heroicons/react/outline'
import CursorImage from '../../cursor.png'
import CursorExampleImage from '../../cursor_example.png'
import CustomPuzzleImage from '../../custompuzzle.png'
import UnlimitedImage from '../../unlimited.png'
import ArchiveImage from '../../archive.png'
import MiniImage from '../../mini.png'
import HighContrastDarkImage from '../../highcontrastdark.png'
import HighContrastImage from '../../highcontrast.png'
import MultiCursorImage from '../../multicursor.png'
import { useEffect } from 'react';
import { useAchievements } from '../../context/AchievementsContext'

type Props = {
  user: User
  isDarkMode: boolean
}

export const Tips = ({
  user,
  isDarkMode
}: Props) => {
  const {grantKeyedAchievement} = useAchievements() 

  useEffect(() => {
    return () => {
      grantKeyedAchievement(user, "knowledge")
    }
  }, [user, grantKeyedAchievement])

  const puzzle1 = getPuzzle("boneless")
  const puzzle2 = getPuzzle("goodbyes")
  const puzzle3 = getPuzzle("potatoes")
  const puzzle4 = getPuzzle("passages")
  const link = (text: string, href: string) => {
        return <a href={href} target="_blank" rel="noreferrer" className="text-indigo-700 dark:text-indigo-400 font-bold text-xs">{text}</a>
  }

  return <div className="p-4 sm:pb-8 sm:px-6 lg:px-8 flex flex-col grow">
    <div id="container" className="flex grow w-full flex-row justify-evenly">
      <div className="w-full sm:p-4 sm:w-1/2 flex flex-col dark:text-gray-300">
        <h1 className="title text-4xl my-8 font-bold text-center">Tips and Tricks</h1>
        <h3 className="title text-xl my-2 font-bold">Filler words</h3>
        <p>
          A common way to play Polygonle is by using a filler word to gain as much information as possible. If you're having trouble getting started with a solve, consider using a filler word or two to narrow down the possible solutions. Give some of these filler words a try!
        </p>
        <div className="grid grid-cols-3 p-4">
          <div className="p-4">
            <h5 className="text-lg font-bold">6 letters</h5>
            <ul className="flex flex-wrap">
              <li className="px-2">almost</li>
              <li className="px-2">arouse</li>
              <li className="px-2">during</li>
              <li className="px-2">easier</li>
              <li className="px-2">family</li>
              <li className="px-2">flower</li>
              <li className="px-2">itself</li>
              <li className="px-2">number</li>
              <li className="px-2">reason</li>
              <li className="px-2">scream</li>
              <li className="px-2">second</li>
              <li className="px-2">talent</li>
              <li className="px-2">united</li>
            </ul>
          </div>
          <div className="p-4">
            <h5 className="text-lg font-bold">7 letters</h5>
            <ul className="flex flex-wrap">
              <li className="px-2">acrobat</li>
              <li className="px-2">brownie</li>
              <li className="px-2">curious</li>
              <li className="px-2">fortune</li>
              <li className="px-2">general</li>
              <li className="px-2">habitat</li>
              <li className="px-2">mermaid</li>
              <li className="px-2">obvious</li>
              <li className="px-2">phoenix</li>
              <li className="px-2">poultry</li>
              <li className="px-2">protein</li>
              <li className="px-2">rainbow</li>
              <li className="px-2">society</li>
              <li className="px-2">special</li>
              <li className="px-2">through</li>
              <li className="px-2">unicorn</li>
              <li className="px-2">working</li>
            </ul>
          </div>
          <div className="p-4">
            <h5 className="text-lg font-bold">8 letters</h5>
            <ul className="flex flex-wrap">
              <li className="px-2">abstract</li>
              <li className="px-2">behavior</li>
              <li className="px-2">calendar</li>
              <li className="px-2">children</li>
              <li className="px-2">machined</li>
              <li className="px-2">material</li>
              <li className="px-2">medicine</li>
              <li className="px-2">national</li>
              <li className="px-2">obtained</li>
              <li className="px-2">physical</li>
              <li className="px-2">precious</li>
              <li className="px-2">proteins</li>
              <li className="px-2">rainbows</li>
              <li className="px-2">robustly</li>
              <li className="px-2">sunshine</li>
              <li className="px-2">whatever</li>
            </ul>
          </div>
        </div>
        <p>
          You can also try using a pair of filler words with all unique letters. This will give you good chances of solving the puzzle in 3 or 4 guesses.
        </p>
        <div className="grid grid-cols-3 p-4">
          <div className="p-4">
            <h5 className="text-lg font-bold">6 letters</h5>
            <ul className="flex flex-wrap">
              <li className="px-2">botany & cruise</li>
            </ul>
          </div>
          <div className="p-4">
            <h5 className="text-lg font-bold">7 letters</h5>
            <ul className="flex flex-wrap">
              <li className="px-2">machine & poultry</li>
            </ul>
          </div>
          <div className="p-4">
            <h5 className="text-lg font-bold">8 letters</h5>
            <ul className="flex flex-wrap">
              <li className="px-2">machined & robustly</li>
            </ul>
          </div>
        </div>

        <div className="flex flex-wrap text-xs">
          Credit to &nbsp; {link("@supashleyc", "https://twitter.com/supashleyc/status/1650942143408504832")}&nbsp;{link("@rangsk", "https://www.youtube.com/rangsk")} &nbsp;{link("@morganpuzzle", "https://www.tiktok.com/@morganpuzzle")} &nbsp;{link("@dailywordle", "https://www.tiktok.com/@dailywordle")}&nbsp;{link("@michael.dicostanzo", "https://www.tiktok.com/@michael.dicostanzo")} &nbsp;{link("@theasylm", "https://youtube.com/theasylm")}&nbsp;for these ideas
        </div>
        <h3 className="title text-xl my-8 font-bold dark:text-gray-300">Prefixes & suffixes</h3>
        <p>
          One key strategy for solving Polygonle puzzles in fewer guesses and on harder difficulty modes is testing common prefixes & suffixes. This works especially well if one of the characters in the prefix or sufix is also a duplicate character in the puzzle. Try testing some of these common prefixes or suffixes on your next solve!
        </p>
        <div className="p-4">
          <h5 className="text-lg font-bold">Prefixes</h5>
          <ul className="flex flex-col">
            <li><span className="italic font-bold">un-</span>: Used to form opposites (e.g. unhappy, unable, unfair).</li>
            <li><span className="italic font-bold">re-</span>: Used to indicate repetition or a return to a previous state (e.g. redo, rebuild, return).</li>
            <li><span className="italic font-bold">dis-</span>: Used to indicate negation or reversal (e.g. disagree, disapprove, disappear).</li>
            <li><span className="italic font-bold">pre-</span>: Used to indicate before or in advance (e.g. preview, preheat, predict).</li>
            <li><span className="italic font-bold">post-</span>: Used to indicate after or later (e.g. postgraduate, postwar, postscript).</li>
            <li><span className="italic font-bold">ex-</span>: Used to indicate former or out of (e.g. ex-husband, ex-president, ex-employee).</li>
            <li><span className="italic font-bold">in-/im-/il-/ir-</span>: Used to indicate negation, not or opposite of (e.g. inaction, impossible, illiterate, irregular).</li>
            <li><span className="italic font-bold">co-/com-/con-</span>: Used to indicate together, with or partnership (e.g. cooperate, combine, connect).</li>
            <li><span className="italic font-bold">mis-</span>: Used to indicate wrongly or badly (e.g. misbehave, mistake, misinterpret).</li>
            <li><span className="italic font-bold">over-</span>: Used to indicate excess or too much (e.g. overcook, overestimate, overreact).</li>
          </ul>
        </div>
        <div className="p-4">
          <h5 className="text-lg font-bold">Suffixes</h5>
          <ul className="flex flex-col">
            <li><span className="italic font-bold">-ing</span>: Used to form present participles and gerunds (e.g. running, swimming, reading).</li>
            <li><span className="italic font-bold">-ed</span>: Used to form the past tense of regular verbs (e.g. walked, played, talked).</li>
            <li><span className="italic font-bold">-ly</span>: Used to form adverbs (e.g. quickly, slowly, nicely).</li>
            <li><span className="italic font-bold">-s/-es</span>: Used to form plurals of nouns and the third person singular present tense of verbs (e.g. dogs, cats, runs, eats).</li>
            <li><span className="italic font-bold">-ment</span>: Used to form nouns indicating an action, process, or result (e.g. development, improvement, excitement).</li>
            <li><span className="italic font-bold">-able/-ible</span>: Used to form adjectives indicating the ability or capability of something (e.g. capable, flexible, incredible).</li>
            <li><span className="italic font-bold">-ful/-less</span>: Used to form adjectives indicating the presence or absence of something (e.g. hopeful, useless, fearless).</li>
            <li><span className="italic font-bold">-ion/-tion/-ation/-ition</span>: Used to form nouns indicating an action, process, or state (e.g. conversation, celebration, competition, definition).</li>
            <li><span className="italic font-bold">-al/-ial</span>: Used to form adjectives indicating a relationship or characteristic (e.g. social, financial, special).</li>
            <li><span className="italic font-bold">-ize/-ise</span>: Used to form verbs indicating a process or action (e.g. realize, criticize, organize).</li>
          </ul>
        </div>
        <h3 className="title text-xl my-8 font-bold dark:text-gray-300">The cursor</h3>
        <div className="flex flex-col items-center justify-center">
          <img className="m-4 w-96 border-4" src={CursorImage} alt="cursor" />
          <p>Did you know Polygonle has a character cursor which lets you visualize possible words without filling in every letter? You can click on an individual tile to fill it in and use the spacebar or arrow keys to navigate the cursor without needing to type a specific letter. Here's an example of partially completing a word using the cursor.</p>
          <img className="m-4 w-96 border-4" src={CursorExampleImage} alt="cursor example" />
        </div>
        <h3 className="title text-xl my-8 font-bold dark:text-gray-300">Duplicate letters</h3>
        <p>Every word in Polygonle will contain at least one set of duplicate letters. The location of the duplicate characters should help you narrow down possible solutions to the puzzle. For example, here are the most frequently occuring adjacent pairs of duplicate letters</p>
            <ul className="flex flex-wrap p-2">
              <li className="px-2 font-bold">ss <span className="font-normal">(22.7%)</span></li>
              <li className="px-2 font-bold">ll <span className="font-normal">(18.6%)</span></li>
              <li className="px-2 font-bold">oo <span className="font-normal">(9.2%)</span></li>
              <li className="px-2 font-bold">ee <span className="font-normal">(8.0%)</span></li>
              <li className="px-2 font-bold">tt <span className="font-normal">(7.2%)</span></li>
              <li className="px-2 font-bold">rr <span className="font-normal">(5.7%)</span></li>
              <li className="px-2 font-bold">pp <span className="font-normal">(4.2%)</span></li>
              <li className="px-2 font-bold">nn <span className="font-normal">(4.1%)</span></li>
              <li className="px-2 font-bold">mm <span className="font-normal">(4.0%)</span></li>
              <li className="px-2 font-bold">ff <span className="font-normal">(3.7%)</span></li>
            </ul>
        <h4 className="title text-lg my-4 font-bold dark:text-gray-300">Guessing with duplicates #1</h4>
            <p className="mt-4">Unlike in other word games, a guess containing duplicate letters can give a tremendous amount of information in Polygonle.</p>
            <p>Consider the following guess:</p>
      <div className="m-8 flex flex-col">
        <div className="pt-4">
          <Puzzle isDarkMode={isDarkMode} puzzle={puzzle1} />
        </div>
        <div className="flex justify-center mb-1">
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="E"
            status="present"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="M"
            status="absent"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="I"
            status="absent"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="S"
            status="present"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="S"
            status="present"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="I"
            status="absent"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="O"
            status="present"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="N"
            status="present"
          />
        </div>
      </div>
      <p>Believe it or not, with this one guess we've actually tested 3 possible locations for the letter S! We directly test the duplicate yellow triangle and the red triangle positions, and in this case both S are in the word but in the wrong location. In Polygonle this means that the letter S must be duplicated in the final word, and there is only one other duplicate shape - the purple hexagons. So we can deduce that the purple hexagons must both be the letter S. </p>
      <div className="col-span-1 m-8 flex items-center justify-center">
        <Cell />
        <Cell />
        <Cell />
        <Cell />
        <Cell />
        <Cell />
        <Cell
          isRevealing={true}
          isCompleted={true}
          value="S"
          status="correct"
        />
        <Cell
          isRevealing={true}
          isCompleted={true}
          value="S"
          status="correct"
        />
      </div>
        <h4 className="title text-lg my-4 font-bold dark:text-gray-300">Guessing with duplicates #2</h4>
        <p className="mt-4">If your guess contains a duplicate letter, but that letter is not duplicated in the final solution, the tiles will show this information.</p>
      <div className="m-8 flex flex-col">
        <div className="pt-4">
          <Puzzle isDarkMode={isDarkMode} puzzle={puzzle2} />
        </div>
        <div className="flex justify-center mb-1">
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="S"
            status="present"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="T"
            status="absent"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="A"
            status="absent"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="N"
            status="absent"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="D"
            status="present"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="A"
            status="absent"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="R"
            status="absent"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="D"
            status="absent"
          />
        </div>
      </div>
      <p>Here we know that the letter A is not found in the solution as both tiles are gray.</p>
      <p>We also know that there is only a single letter D in the solution. If the D were duplicated, both tiles under each letter D would be yellow.
      </p>
      <h3 className="title text-xl my-8 font-bold dark:text-gray-300">Game modes</h3>
      <h4 className="title text-lg my-4 font-bold dark:text-gray-300">Daily</h4>
      <p>The original polygonle puzzle format. Words are 6 to 8 letters long and will always contain at least one duplicate letter. A new puzzle appears at midnight (based on your local timezone). </p>
      <h4 className="title text-lg my-4 font-bold dark:text-gray-300">Unlimited</h4>
      <div className="flex flex-col items-center justify-center">
        <img className="my-2 w-96 border-4" src={UnlimitedImage} alt="archive" />
      </div>
      <p>Tired of waiting for the daily puzzle, try unlimited mode. As the name suggests, you can solve unlimited puzzles to your heart's content. The set of possible puzzles includes past and future daily puzzles. Use the refresh button in the upper left to load a new puzzle.</p>
      <h4 className="title text-lg my-4 font-bold dark:text-gray-300">Custom</h4>
      <div className="flex flex-col items-center justify-center">
        <img className="my-2 w-96 border-4" src={CustomPuzzleImage} alt="archive" />
      </div>
      <p>Create a custom puzzle to stump your friends and family. Supports words 5-8 letters long. 8 letter words require at least one duplicate letter. Use the refresh icon to see a different combination of shapes and colors. You can also give your custom puzzle a title or provide a hint. Custom puzzles do not count toward your overall streak or game statistics.</p>
      <a className="text-indigo-700 dark:text-indigo-400 font-bold" href="https://www.polygonle.com/custom/eyJ3b3JkIjoiUE9UQVRPRVMiLCJzZWVkIjoiZWY2Njk2Y2UiLCJ0aXRsZSI6IkRlZmluaXRlbHkgTm90IFBvdGF0b2VzIiwiY3JlYXRvciI6IiIsImhpbnQiOiJJdCdzIGEgZGVsaWNpb3VzIGZvb2QifQ==/ef6696ce">Try a custom puzzle</a>
      <h4 className="title text-lg my-4 font-bold dark:text-gray-300">Mini (Polygonle+)</h4>
      <div className="flex flex-col items-center justify-center">
       <img className="my-2 w-96 border-4" src={MiniImage} alt="mini" />
      </div>
      <p>A shorter variant of the daily puzzle for Polygonle+ users. Words are 5 letters long and will always contain at least one duplicate letter. A new puzzle appears at midnight (based on your local timezone).</p>
      <h4 className="title text-lg my-4 font-bold dark:text-gray-300">Archive (Polygonle+)</h4>
      <div className="flex flex-col items-center justify-center">
        <img className="my-2 w-96 border-4" src={ArchiveImage} alt="archive" />
      </div>
      <p>Play any previous daily puzzle based on the puzzle's number or date. Click the calendar button to navigate to a puzzle on a specific date.</p>
      <h3 className="title text-xl my-8 font-bold dark:text-gray-300">Settings</h3>
      <p>
        You can access game settings by clicking the <CogIcon className="inline-flex h-6 w-6 scale-up cursor-pointer dark:stroke-white" /> icon in the upper right corner.
      </p>
      <h4 className="title text-lg my-4 font-bold dark:text-gray-300">Hard mode</h4>
      <p>If you're looking for a greater challenge consider enabling hard mode. In hard mode any revealed hints must be used in subsequent guesses</p>
      <div className="m-8 flex flex-col">
        <div className="pt-4">
          <Puzzle isDarkMode={isDarkMode} puzzle={puzzle3} />
        </div>
        <div className="flex justify-center mb-1">
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="P"
            status="correct"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="H"
            status="absent"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="Y"
            status="absent"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="S"
            status="present"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="I"
            status="absent"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="C"
            status="absent"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="A"
            status="present"
          />
          <Cell
            isRevealing={true}
            isCompleted={true}
            value="L"
            status="absent"
          />
        </div>
      </div>
      <p>On hard mode we need to use the letter P in the first position and include the letters S and A on our next guess.</p>
      <h4 className="title text-lg my-4 font-bold dark:text-gray-300">Expert mode</h4>
      <p>In expert mode every guess must conform to the repeated pattern of shapes in the puzzle.</p>
      <div className="m-8 flex flex-col">
        <div className="pt-4">
          <Puzzle isDarkMode={isDarkMode} puzzle={puzzle4} />
        </div>
      </div>
      <p>For this puzzle only guesses like 'borrower', 'desserts', 'missions', 'passages' would be accepted. Where the letters repeat in an identical pattern to that of the puzzle. A typical guess like 'sunshine' or 'rainbow' would not be accepted in this format.</p>
      <p>It's typical to take a long time to arrive at a possible guess in expert mode. For some puzzles there may only be a few possible words which fit this pattern, so although it will take a while to get a correct guess you'll likely solve the puzzle in few guesses. Using the prefix/suffix strategies outlined above are critical in this format.</p>
      <h4 className="title text-lg my-4 font-bold dark:text-gray-300">Hexpert mode</h4>
      <p>Enabling both hard mode and expert mode activates 'hexpert mode'. This is a combination of both modes for additional difficulty. This is the hardest mode Polygonle offers.</p>
      <h4 className="title text-lg my-4 font-bold dark:text-gray-300">High contrast mode</h4>
      <p>High contrast mode changes the colors of revealed tiles for better legibility.</p>
      <div className="flex flex-col items-center justify-center">
        <img className="my-2 w-96 border-4" src={HighContrastImage} alt="highcontrast" />
        <img className="my-2 w-96 border-4" src={HighContrastDarkImage} alt="highcontrastdark" />
      </div>
      <p> Orange tiles indicate the letter is in the correct location. Cyan tiles indicate the letter is in the word but not the correct location.</p>
      <h4 className="title text-lg my-4 font-bold dark:text-gray-300">Multi-cursor mode</h4>
      <p>Polygonle supports a multiple cursor mode which allows you to simultaneously fill in all matching shapes with a letter. This is very helpful for visualizing possible letters in specific locations.</p>
      <div className="flex flex-col items-center justify-center">
        <img className="my-2 w-96 border-4" src={MultiCursorImage} alt="highcontrast" />
      </div>
      <h5 className="title my-4 font-bold dark:text-gray-300">Hotkey</h5>
      <p>Holding [shift] enables multi-cursor mode when the setting is not enabled.</p>
      </div>
    </div>
  </div>
}

